import { Link } from "react-router-dom";
import { COMPANY } from "@/lib/site";
import { ArrowUpRight, MapPin, Building, Users, Award } from "lucide-react";
import Seo from "@/components/Seo";

const VALUES = [
  { icon: Award, t: "Stručnost", d: "Tim ovlaštenih inženjera arhitekture, geodezije i građevine s dugogodišnjim iskustvom u upravnim postupcima." },
  { icon: Users, t: "Transparentnost", d: "Sve cijene, rokovi i obveze jasno definirane od početka — bez naknadnih iznenađenja." },
  { icon: Building, t: "Lokalno znanje", d: "Specijalizirani za priobalje i dalmatinske otoke — poznavanje lokalnih prostornih planova." },
  { icon: MapPin, t: "Dalmacija", d: "Od Zadra do Dubrovnika — pokrivamo sve katastarske urede u Dalmaciji." },
];

const TEAM = [
  { name: "Silvio Bašić", role: "Ovl. inženjer geodezije", years: "20+ god." },
  { name: "Vlado Krstulović", role: "Ovlašteni arhitekt", years: "20+ god." },
  { name: "Nikola Medić", role: "Ovl. inženjer građevinarstva", years: "" },
  { name: "Petar Sapunar", role: "Dipl. inženjer geodezije", years: "" },
];

export default function ONamaPage() {
  return (
    <div data-testid="o-nama-page">
      <Seo
        title="O nama"
        description="Infokarta d.o.o. je geodetski ured iz Splita s više od 20 godina iskustva i preko 2.000 provedenih postupaka legalizacije u Dalmaciji."
        path="/o-nama"
      />
      {/* HERO */}
      <section className="border-b border-hair">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="overline text-ink-soft flex items-center gap-3">
                <span className="w-8 h-px bg-ink-soft/40" /> O nama
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-bold text-ink mt-6">
                Više od dva<br />
                <span className="text-ink/40">desetljeća geodezije</span><br />
                i legalizacije.
              </h1>
            </div>
            <div className="md:col-span-4 text-ink-soft leading-relaxed">
              {COMPANY.name} posluje od <span className="text-ink font-medium">2002. godine</span> u
              Splitu. Pružamo kompletne <span className="text-ink font-medium">geodetske usluge</span> i
              vodimo <span className="text-ink font-medium">postupke legalizacije</span> objekata na
              području <span className="text-ink font-medium">Dalmacije — od Zadra do Dubrovnika</span>.
            </div>
          </div>
        </div>
      </section>

      {/* STORY + IMAGE bento */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-7 border border-hair p-8 lg:p-12 bg-white">
            <div className="overline text-ink-soft">Naša priča</div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-semibold text-ink mt-4 leading-tight">
              Geodetski ured s preko 2.000 provedenih postupaka legalizacije.
            </h2>
            <div className="text-ink-soft mt-6 leading-relaxed space-y-4">
              <p>
                Od 2002. godine bavimo se geodetskim poslovima i legalizacijom
                nezakonito izgrađenih objekata. Kroz više od dva desetljeća rada
                proveli smo preko <span className="text-ink font-medium">2.000 projekata legalizacije</span> —
                od malih pomoćnih zgrada na otocima do većih stambenih i gospodarskih objekata.
              </p>
              <p>
                Uz legalizaciju, izrađujemo i sve vrste geodetskih elaborata:
                snimke izvedenog stanja, parcelacijske elaborate, elaborate za upis
                objekata u katastar i zemljišnu knjigu, geodetske podloge za projekte
                te iskolčenja građevina.
              </p>
              <p>
                Vjerujemo da se pravna sigurnost gradi na točnoj mjeri i jasnoj
                dokumentaciji — zato u svakom predmetu inzistiramo na potpunom
                pregledu situacije prije nego započnemo postupak.
              </p>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden border border-hair">
              <img
                src="/img/about.webp"
                alt="Arhitektonski stol s nacrtima"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="surface border-t border-hair">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-6">
              <div className="overline text-ink-soft">Vrijednosti</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold text-ink mt-4 leading-[1.05]">
                Što nas razlikuje.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-ink/10 mt-12 border border-ink/10">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.t} className="bg-white p-8 hover-lift">
                  <Icon className="w-6 h-6 text-ink" strokeWidth={1.5} />
                  <h3 className="font-display text-xl font-semibold text-ink mt-6">{v.t}</h3>
                  <p className="text-ink-soft text-sm leading-relaxed mt-2">{v.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 border-t border-hair">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-6">
            <div className="overline text-ink-soft">Tim</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold text-ink mt-4 leading-[1.05]">
              Ljudi iza svakog predmeta.
            </h2>
          </div>
          <div className="md:col-span-6 text-ink-soft leading-relaxed">
            Multidisciplinarni tim u kojem se znanje arhitekata, geodeta i pravnika
            spaja u jedan koherentan proces. Svaki predmet vodi jedan voditelj —
            vaša točka kontakta od početka do kraja.
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
          {TEAM.map((m, i) => (
            <div key={m.name} className="border border-hair p-6 bg-white hover-lift">
              <div className="aspect-square bg-ink/5 flex items-center justify-center">
                <span className="font-display text-6xl text-ink/30 font-bold">
                  {m.name.split(" ").map((n) => n[0]).join("")}
                </span>
              </div>
              <div className="mt-5">
                <div className="overline text-ink-soft">0{i + 1}</div>
                <div className="font-display font-semibold text-ink mt-2">{m.name}</div>
                <div className="text-ink-soft text-sm">{m.role}</div>
                {m.years && <div className="text-ink-soft text-xs mt-1">Iskustvo · {m.years}</div>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CONTACT BOX */}
      <section className="border-t border-hair bg-ink text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="overline text-white/60">Ured Split</div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-semibold mt-3 leading-tight">
              {COMPANY.address} — dođite na konzultacije bez obveze.
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/prijava"
              data-testid="o-nama-cta"
              className="inline-flex items-center gap-2 bg-white text-ink px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-white/90 transition-colors"
            >
              Prijavi se
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
